"use client";

import React from "react";
import { SlidersHorizontal as Sliders, Layers, FileCheck, Factory, BookOpen, Download, Calculator, ArrowRight, ArrowDown, LineChart as ChartLine, TableProperties } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
interface ResourceCardItem {
  id: string;
  title: Record<"en" | "zh" | "es" | "de" | "fr" | "pt", string>;
  desc: Record<"en" | "zh" | "es" | "de" | "fr" | "pt", string>;
  to: string;
  iconName: "Sliders" | "Layers" | "FileCheck" | "Factory" | "BookOpen" | "Download" | "Calculator" | "ChartLine" | "TableProperties";
  specTag: Record<"en" | "zh" | "es" | "de" | "fr" | "pt", string>;
}
const resourceItems: ResourceCardItem[] = [{
  id: "tools",
  title: {
    en: "Engineering Tools",
    zh: "工程工具与计算模型",
    es: "Herramientas de ingeniería",
    de: "Engineering-Werkzeuge",
    fr: "Outils d'ingénierie",
    pt: "Ferramentas de engenharia"
  },
  desc: {
    en: "Dedicated calculation suite covering Delta P models, engineering formulas, aperture charts, and product selection roadmaps.",
    zh: "涵盖压差模型、工程公式、孔径图表及产品架构选型路线的专业工程工具集。",
    es: "Suite de cálculo especializada con modelos Delta-P, fórmulas de ingeniería, tablas de abertura y guías de selección.",
    de: "Spezialisierte Berechnungssuite mit Delta-P-Modellen, Engineering-Formeln, Maschenweitentabellen und Auswahlleitfäden.",
    fr: "Suite de calcul spécialisée comprenant modèles Delta-P, formules d'ingénierie, abaques d'ouverture et guides de sélection.",
    pt: "Pacote especializado de cálculos com modelos Delta-P, fórmulas de engenharia, tabelas de abertura e roteiros de seleção."
  },
  to: "/ToolsPage",
  iconName: "Calculator",
  specTag: {
    en: "Engineering Tools",
    zh: "工程工具",
    es: "Herramientas",
    de: "Werkzeuge",
    fr: "Outils",
    pt: "Ferramentas"
  }
}, {
  id: "datasheets",
  title: {
    en: "Datasheets & Spec Tables",
    zh: "技术数据表与规格对照",
    es: "Fichas técnicas y tablas de especificaciones",
    de: "Datenblätter & Spezifikationstabellen",
    fr: "Fiches techniques et tables de spécifications",
    pt: "Fichas técnicas e tabelas de especificações"
  },
  desc: {
    en: "Comprehensive technical datasheets covering mesh counts, wire diameters, aperture tolerances, and mechanical parameters.",
    zh: "涵盖丝网目数、公称丝径、开孔公差及机械强度的完整技术数据表。",
    es: "Fichas técnicas completas con número de mallas, diámetros de alambre, tolerancias de abertura y parámetros mecánicos.",
    de: "Umfassende technische Datenblätter mit Maschenzahlen, Drahtdurchmessern, Maschenweitentoleranzen und mechanischen Parametern.",
    fr: "Fiches techniques complètes précisant le nombre de mailles, les diamètres de fil, les tolérances d'ouverture et les paramètres mécaniques.",
    pt: "Fichas técnicas completas abrangendo contagem de malha, diâmetros de arame, tolerâncias de abertura e parâmetros mecânicos."
  },
  to: "/DatasheetsPage",
  iconName: "TableProperties",
  specTag: {
    en: "Datasheets",
    zh: "数据表",
    es: "Fichas técnicas",
    de: "Datenblätter",
    fr: "Fiches techniques",
    pt: "Fichas técnicas"
  }
}, {
  id: "technical-charts",
  title: {
    en: "Technical Charts",
    zh: "工程图表库",
    es: "Gráficos técnicos",
    de: "Technische Diagramme",
    fr: "Abaques techniques",
    pt: "Gráficos técnicos"
  },
  desc: {
    en: "Micron-to-mesh conversion charts, open area comparisons, and differential pressure curves for wire mesh and filter cloth.",
    zh: "微米与目数转换图、开孔率对比及编织丝网与滤布流阻压降曲线。",
    es: "Gráficos de conversión de micras a mallas, comparativas de área abierta y curvas de presión diferencial.",
    de: "Umrechnungsdiagramme Mikron/Mesh, Vergleiche offener Siebflächen und Differenzdruckkurven.",
    fr: "Abaques de conversion microns/mailles, comparaisons de surface ouverte et courbes de perte de charge.",
    pt: "Gráficos de conversão de mícrons para mesh, comparações de área aberta e curvas de pressão diferencial."
  },
  to: "/TechnicalChartsPage",
  iconName: "ChartLine",
  specTag: {
    en: "Charts & Curves",
    zh: "图表与曲线",
    es: "Gráficos y curvas",
    de: "Diagramme & Kurven",
    fr: "Abaques et courbes",
    pt: "Gráficos e curvas"
  }
}, {
  id: "calculation-guides",
  title: {
    en: "Calculation Guides",
    zh: "计算指南与算法",
    es: "Guías de cálculo",
    de: "Berechnungsleitfäden",
    fr: "Guides de calcul",
    pt: "Guias de cálculo"
  },
  desc: {
    en: "Engineering calculation methods for open area percentage, hydraulic resistance, particle retention, and flow velocities.",
    zh: "开孔率百分比、水力流阻、颗粒截留等级及通流速度工程计算方法。",
    es: "Métodos de cálculo de ingeniería para porcentaje de área abierta, resistencia hidráulica y retención de partículas.",
    de: "Technische Berechnungsmethoden für offene Siebfläche, Durchflusswiderstand, Partikelabscheidung und Strömungsgeschwindigkeit.",
    fr: "Méthodes de calcul technique pour le pourcentage de surface ouverte, la résistance hydraulique et la rétention de particules.",
    pt: "Métodos de cálculo de engenharia para porcentagem de área aberta, resistência hidráulica e retenção de partículas."
  },
  to: "/CalculationGuidesPage",
  iconName: "Calculator",
  specTag: {
    en: "Engineering Guides",
    zh: "工程指南",
    es: "Guías de ingeniería",
    de: "Engineering-Leitfäden",
    fr: "Guides techniques",
    pt: "Guias de engenharia"
  }
}, {
  id: "product-selection",
  title: {
    en: "Product Selection",
    zh: "产品选型",
    es: "Selección de productos",
    de: "Produktauswahl",
    fr: "Sélection de produits",
    pt: "Seleção de produtos"
  },
  desc: {
    en: "Comprehensive selection matrices and engineering criteria to specify optimal mesh weave types and aperture geometries for targeted industrial filtration duties.",
    zh: "按过滤精度、流量需求与截留要求，系统化选择匹配工况的精密丝网类型与结构规格。",
    es: "Matrices completas de selección y criterios de ingeniería para especificar tipos de tejido y geometrías de abertura para tareas industriales de filtración.",
    de: "Umfassende Auswahlmatrizen und Engineering-Kriterien zur Spezifikation optimaler Drahtgewebetypen und Maschengeometrien für industrielle Filtrationsaufgaben.",
    fr: "Matrices de sélection complètes et critères d'ingénierie pour définir les types de tissage et géométries d'ouverture adaptés aux exigences de filtration industrielle.",
    pt: "Matrizes abrangentes de seleção e critérios de engenharia para especificar tipos de trama e geometrias de abertura para aplicações de filtração industrial."
  },
  to: "/ProductSelectionPage",
  iconName: "Sliders",
  specTag: {
    en: "Selection Matrix",
    zh: "选型矩阵",
    es: "Matriz de selección",
    de: "Auswahlmatrix",
    fr: "Matrice de sélection",
    pt: "Matriz de seleção"
  }
}, {
  id: "materials",
  title: {
    en: "Materials & Grades",
    zh: "材质等级",
    es: "Materiales y grados",
    de: "Werkstoffe & Güten",
    fr: "Matériaux et nuances",
    pt: "Materiais e graus"
  },
  desc: {
    en: "Metallurgical data sheets, chemical compositions, and corrosion resistance guidelines across austenitic stainless steels and high-performance alloys.",
    zh: "涵盖 304、304L、316、316L 及耐蚀合金的化学成分、机械强度与耐温抗腐蚀性能数据。",
    es: "Fichas metalúrgicas, composiciones químicas y guías de resistencia a la corrosión para aceros inoxidables austeníticos y aleaciones de alto rendimiento.",
    de: "Metallurgische Datenblätter, chemische Zusammensetzungen und Korrosionsbeständigkeitsrichtlinien für austenitische Edelstähle und Legierungen.",
    fr: "Fiches techniques métallurgiques, compositions chimiques et critères de résistance à la corrosion pour aciers inoxydables austénitiques et alliages.",
    pt: "Fichas metalúrgicas, composições químicas e diretrizes de resistência à corrosão para aços inoxidáveis austeníticos e ligas de alto desempenho."
  },
  to: "/MaterialsAndGradesPage",
  iconName: "Layers",
  specTag: {
    en: "304 / 316 / 316L",
    zh: "304 / 316 / 316L",
    es: "304 / 316 / 316L",
    de: "304 / 316 / 316L",
    fr: "304 / 316 / 316L",
    pt: "304 / 316 / 316L"
  }
}, {
  id: "mesh-filtration-fundamentals",
  title: {
    en: "Mesh & Filtration Fundamentals",
    zh: "网孔与过滤基础知识",
    es: "Fundamentos de malla y filtración",
    de: "Grundlagen zu Drahtgewebe & Filtration",
    fr: "Fondamentaux de la toile et de la filtration",
    pt: "Fundamentos de malha e filtração"
  },
  desc: {
    en: "Core concepts covering mesh count and aperture, wire diameter and open area, and micron rating fundamentals for understanding filtration performance.",
    zh: "涵盖目数与孔径、丝径与开孔率、微米等级等过滤性能基础知识。",
    es: "Conceptos clave sobre número de mallas, abertura, diámetro de alambre, área abierta y retención en micras para comprender el rendimiento de filtración.",
    de: "Grundlegende Konzepte zu Maschenzahl und Maschenweite, Drahtdurchmesser und offener Siebfläche sowie Mikron-Abscheidegraden zur Bewertung der Filtrationsleistung.",
    fr: "Notions essentielles sur le nombre de mailles, l'ouverture, le diamètre de fil, la surface ouverte et le seuil de filtration en microns pour évaluer les performances.",
    pt: "Conceitos fundamentais sobre contagem de malha, abertura, diâmetro do arame, área aberta e classificação em mícrons para entender a eficiência de filtração."
  },
  to: "/MeshAndFiltrationFundamentalsPage",
  iconName: "BookOpen",
  specTag: {
    en: "Core Concepts",
    zh: "基础知识",
    es: "Conceptos clave",
    de: "Grundlagen",
    fr: "Notions clés",
    pt: "Conceitos-chave"
  }
}, {
  id: "specifications-standards",
  title: {
    en: "Specifications & Standards",
    zh: "规格与国际标准",
    es: "Especificaciones y normas",
    de: "Spezifikationen & Normen",
    fr: "Spécifications et normes",
    pt: "Especificações e normas"
  },
  desc: {
    en: "Reference technical tables for ASTM E2016, ISO 9044, and DIN standards detailing wire diameters, aperture tolerances, and mechanical criteria.",
    zh: "全面对照 ASTM E2016、ISO 9044 及 DIN 工业标准，规范丝径公差与开孔几何参数。",
    es: "Tablas de referencia técnica para normas ASTM E2016, ISO 9044 y DIN con tolerancias de alambre, aberturas y criterios mecánicos.",
    de: "Referenztabellen für ASTM E2016, ISO 9044 und DIN-Normen mit Drahtdurchmessern, Maschenweitentoleranzen und mechanischen Kriterien.",
    fr: "Tables de référence technique pour les normes ASTM E2016, ISO 9044 et DIN précisant diamètres de fil, tolérances d'ouverture et critères mécaniques.",
    pt: "Tabelas de referência técnica para normas ASTM E2016, ISO 9044 e DIN detalhando tolerâncias de diâmetro de arame, aberturas e critérios mecânicos."
  },
  to: "/SpecificationsStandardsPage",
  iconName: "FileCheck",
  specTag: {
    en: "ASTM / ISO / DIN",
    zh: "ASTM / ISO / DIN",
    es: "ASTM / ISO / DIN",
    de: "ASTM / ISO / DIN",
    fr: "ASTM / ISO / DIN",
    pt: "ASTM / ISO / DIN"
  }
}, {
  id: "downloads",
  title: {
    en: "Downloads",
    zh: "资料下载",
    es: "Descargas",
    de: "Downloads",
    fr: "Téléchargements",
    pt: "Downloads"
  },
  desc: {
    en: "Product catalog, technical datasheets, technical charts, and standards references available for download.",
    zh: "提供产品目录、技术数据表、技术图表及标准规范资料下载。",
    es: "Catálogo de productos, fichas técnicas, gráficos de ingeniería y referencias de normas disponibles para su descarga.",
    de: "Produktkatalog, technische Datenblätter, Diagramme und Normenreferenzen zum Herunterladen verfügbar.",
    fr: "Catalogue produits, fiches techniques, abaques et documents normatifs disponibles en téléchargement.",
    pt: "Catálogo de produtos, fichas técnicas, gráficos e referências de normas disponíveis para download."
  },
  to: "/DownloadsPage",
  iconName: "Download",
  specTag: {
    en: "Resource Library",
    zh: "资料库",
    es: "Biblioteca de recursos",
    de: "Ressourcenbibliothek",
    fr: "Bibliothèque de ressources",
    pt: "Biblioteca de recursos"
  }
}, {
  id: "engineering-calculations",
  title: {
    en: "Engineering Calculations",
    zh: "工程计算",
    es: "Cálculos de ingeniería",
    de: "Technische Berechnungen",
    fr: "Calculs d'ingénierie",
    pt: "Cálculos de engenharia"
  },
  desc: {
    en: "Open area, aperture and pressure-drop calculation tools for mesh selection.",
    zh: "用于网型选择的开孔率、孔径与压降计算工具。",
    es: "Herramientas de cálculo de área abierta, abertura y caída de presión para la selección de mallas.",
    de: "Berechnungswerkzeuge für offene Siebfläche, Maschenweite und Druckverlust zur Gewebeauswahl.",
    fr: "Outils de calcul de surface ouverte, d'ouverture et de perte de charge pour le choix des toiles.",
    pt: "Ferramentas de cálculo de área aberta, abertura e queda de pressão para seleção de malhas."
  },
  to: "/EngineeringCalculationsPage",
  iconName: "Calculator",
  specTag: {
    en: "Calculations",
    zh: "计算工具",
    es: "Cálculos",
    de: "Berechnungen",
    fr: "Calculs",
    pt: "Cálculos"
  }
}];
export default function ResourceHub() {
  const {
    lang
  } = useLanguage();
  const eyebrow = pick(lang, {
    en: "ENGINEERING LIBRARY",
    zh: "工程资料库",
    es: "BIBLIOTECA DE INGENIERÍA",
    de: "ENGINEERING-BIBLIOTHEK",
    fr: "BIBLIOTHÈQUE D'INGÉNIERIE",
    pt: "BIBLIOTECA DE ENGENHARIA"
  });
  const heading = pick(lang, {
    en: "Browse by Topic",
    zh: "按主题浏览",
    es: "Explorar por tema",
    de: "Nach Themen durchsuchen",
    fr: "Parcourir par thème",
    pt: "Navegar por tema"
  });
  const lead = pick(lang, {
    en: "Jump straight to the technical area you need — selection criteria, material data, mesh & filtration fundamentals, standards, applications, or downloads.",
    zh: "直接跳转到您需要的技术领域——选型标准、材料数据、网孔与过滤基础知识、行业标准、应用场景或资料下载。",
    es: "Acceda directamente al área técnica que necesita: criterios de selección, datos de materiales, fundamentos de filtración, normas, aplicaciones o descargas.",
    de: "Navigieren Sie direkt zu dem gewünschten Bereich: Auswahlkriterien, Werkstoffdaten, Filtrationsgrundlagen, Normen, Anwendungen oder Downloads.",
    fr: "Accédez directement au domaine technique recherché : critères de sélection, données matériaux, fondamentaux de filtration, normes, applications ou téléchargements.",
    pt: "Acesse diretamente a área técnica desejada: critérios de seleção, dados de materiais, fundamentos de filtração, normas, aplicações ou downloads."
  });
  const jumpToArticles = pick(lang, {
    en: "Browse technical articles",
    zh: "浏览技术文章",
    es: "Explorar artículos técnicos",
    de: "Technische Artikel durchsuchen",
    fr: "Parcourir les articles techniques",
    pt: "Explorar artigos técnicos"
  });
  const exploreResource = pick(lang, {
    en: "Explore Resources",
    zh: "浏览资源",
    es: "Explorar recursos",
    de: "Ressourcen erkunden",
    fr: "Découvrir les ressources",
    pt: "Explorar recursos"
  });
  const renderIcon = (iconName: ResourceCardItem["iconName"]) => {
    switch (iconName) {
      case "Sliders":
        return <Sliders className="size-5 text-primary" aria-hidden="true" />;
      case "Layers":
        return <Layers className="size-5 text-primary" aria-hidden="true" />;
      case "FileCheck":
        return <FileCheck className="size-5 text-primary" aria-hidden="true" />;
      case "Factory":
        return <Factory className="size-5 text-primary" aria-hidden="true" />;
      case "BookOpen":
        return <BookOpen className="size-5 text-primary" aria-hidden="true" />;
      case "Download":
        return <Download className="size-5 text-primary" aria-hidden="true" />;
      case "Calculator":
        return <Calculator className="size-5 text-primary" aria-hidden="true" />;
      case "ChartLine":
        return <ChartLine className="size-5 text-primary" aria-hidden="true" />;
      case "TableProperties":
        return <TableProperties className="size-5 text-primary" aria-hidden="true" />;
    }
  };
  return <section id="resourcehubgrid" data-nav="light" className="bg-background text-foreground mf-section border-b border-border">
      <div className="mf-container">
        <Reveal className="mb-10 md:mb-12">
          <div className="flex flex-col gap-3">
            <span className="mf-caption uppercase tracking-wider text-muted-foreground font-mono">
              {eyebrow}
            </span>
            <h2 className="mf-h2 text-foreground">
              {heading}
            </h2>
            <p className="mf-lead text-muted-foreground max-w-2xl">
              {lead}
            </p>
            <div className="pt-1">
              <Link to="#articles" className="inline-flex items-center gap-2 min-h-[44px] px-4 py-2 rounded-md border border-border bg-card text-foreground text-sm font-medium hover:border-primary/50 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <span>{jumpToArticles}</span>
                <ArrowDown className="size-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {resourceItems.map((item, i) => {
          return <Reveal key={item.id} delay={Math.min(i * 60, 300)} className="h-full">
                <Link to={item.to} className="group block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg">
                  <Card className="h-full bg-card border border-border rounded-lg p-6 md:p-8 flex flex-col justify-between mf-hover-lift">
                    <div>
                      <div className="flex items-center justify-between mb-5">
                        <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                          {renderIcon(item.iconName)}
                        </div>
                        <span className="mf-caption font-mono px-2 py-0.5 rounded bg-muted text-muted-foreground border border-border/40">
                          {pick(lang, item.specTag)}
                        </span>
                      </div>

                      <CardHeader className="p-0 mb-3">
                        <CardTitle className="mf-h3 text-card-foreground group-hover:text-primary transition-colors flex items-center gap-1.5">
                          {pick(lang, item.title)}
                        </CardTitle>
                      </CardHeader>

                      <CardContent className="p-0">
                        <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                          {pick(lang, item.desc)}
                        </CardDescription>
                      </CardContent>
                    </div>

                    <div className="flex items-center justify-between mf-caption font-medium text-foreground group-hover:text-primary transition-colors pt-6 mt-6 border-t border-border/60">
                      <span>{exploreResource}</span>
                      <ArrowRight className="size-4" aria-hidden="true" />
                    </div>
                  </Card>
                </Link>
              </Reveal>;
        })}
        </div>
      </div>
    </section>;
}